import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { authApiClient } from "@/lib/auth";
import { useCompanyStore } from "@/store";
import { Card, CardContent } from "@/components/ui";

interface VerifyEmailSearch {
  token?: string;
  type?: string;
}

/** The token's storage form: 32 bytes of hex. Anything else is not a token this
 *  build ever minted, so it is refused HERE rather than spent on a request. */
const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

/** The 401 arm's marker: a verdict on the TOKEN, never on a session. */
const INVALID_TOKEN = "invalid_token";

/**
 * The landing a COMPANY-audience verification mail points at. It is a ROOT
 * route, beside `/login`: the person arriving may hold no session at all, and
 * the entry gate would terminal-screen them before the token could be spent.
 * The staff app keeps its own `/auth/verify-email` page for a utility-audience
 * signup; the API OPERATIONS both pages post to are the same two and are
 * unchanged.
 */
export const Route = createFileRoute("/verify-email")({
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    token:
      typeof search.token === "string" && TOKEN_PATTERN.test(search.token)
        ? search.token
        : undefined,
    type: typeof search.type === "string" ? search.type : undefined,
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { t } = useTranslation("company");
  const { token, type } = Route.useSearch();
  // A standing session is what decides where the reader goes next, and it is
  // also what makes the store flag worth writing at all.
  const isAuthenticated = useCompanyStore((s) => s.isAuthenticated);
  const setEmailVerified = useCompanyStore((s) => s.setEmailVerified);

  const verify = useMutation({
    mutationFn: async (verifyToken: string) => {
      // `account` is the signup/resend token; anything else is the
      // address-CHANGE token, which has its own operation and its own table.
      const result =
        type === "account"
          ? await authApiClient.POST("/auth/verify-account-email", {
              body: { token: verifyToken },
            })
          : await authApiClient.POST("/auth/verify-email", { body: { token: verifyToken } });
      if (result.error) {
        if (result.response.status === 401) throw new Error(INVALID_TOKEN);
        throw result.error;
      }
    },
    onSuccess: () => {
      // The server is the authority; this catches the store up so the Get
      // started screen stops offering its panel the moment the reader returns.
      setEmailVerified(true);
    },
  });

  // One attempt per token, on arrival: the link IS the action, so there is
  // nothing to confirm and no button to press. The dependency array is the
  // TOKEN alone, and deliberately not the mutation: that object is a fresh
  // reference on every render, so keying on it would re-post a single-use
  // token — the second attempt answers 401 and turns a success into an
  // expired link.
  useEffect(() => {
    if (token === undefined) return;
    verify.mutate(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [token]);

  const invalidToken =
    token === undefined ||
    (verify.isError && verify.error instanceof Error && verify.error.message === INVALID_TOKEN);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <Card>
        <CardContent>
          <div className="max-w-md space-y-4 p-6 text-center">
            <h1 className="text-2xl font-bold text-on-surface">{t("verifyEmail.pageTitle")}</h1>

            {token !== undefined && verify.isPending && (
              <p className="text-sm text-muted">{t("verifyEmail.verifying")}</p>
            )}

            {verify.isSuccess && (
              <p className="text-sm text-on-surface">{t("verifyEmail.verified")}</p>
            )}

            {invalidToken && <p className="text-sm text-danger">{t("verifyEmail.invalidToken")}</p>}

            {verify.isError && !invalidToken && (
              <p className="text-sm text-danger">{t("verifyEmail.genericError")}</p>
            )}

            {/* Where the reader goes next is decided by the session, not by the
                outcome: signed in, the portal resolves their own persona; signed
                out, the sign-in page is the only door they have. */}
            {!verify.isPending &&
              (isAuthenticated ? (
                <Link to="/" className="block text-sm font-medium text-primary">
                  {t("verifyEmail.continueToPortal")}
                </Link>
              ) : (
                <Link to="/login" className="block text-sm font-medium text-primary">
                  {t("verifyEmail.goToSignIn")}
                </Link>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
